import React, { useState } from 'react';
import { Calendar, MapPin, Users, UserCheck, User, LogIn, LogOut, CheckCircle2 } from 'lucide-react';

export default function AppointmentTab({ appointments }) {
    const [subTab, setSubTab] = useState('physical');
    const [virtualSort, setVirtualSort] = useState('duedate');

    // Extract physical and virtual from the passed object.
    // As per user request: if no data is found, show the default/fallback data instead of a blank panel.
    const physicalAppts = (appointments?.physical && appointments.physical.length > 0)
        ? appointments.physical
        : [
            {
                Title: 'NewAppointment_20230525153306',
                StartDate: '1 Sept 2023 10:35:56 - 1:35:56',
                Address: '100, near jalaram society, Jalaram Society, Kubernagar, Katargam, Surat, Gujarat 395004, India',
                FieldExecutives: 'kmukesh343(Mukesh Kumar)',
                CollaboratorName: '34594-user1(User1 last)',
                OwnerName: 'kmukesh343(Mukesh Kumar)',
                CheckIn: '2-Sept-2023 11:45:00',
                CheckOut: '2-Sept-2023 2:45:00',
                OutcomeName: 'Converted'
            }
        ];

    const virtualAppts = (appointments?.virtual && appointments.virtual.length > 0)
        ? appointments.virtual
        : [
            {
                Title: 'Virtual Meeting Demo',
                StartDate: '1 Sept 2023 14:00:00 - 15:00:00',
            }
        ];

    /**
     * Parses the CollaboratorName field which can be:
     *  - A JSON string: '[{"CollaboratorName":"(Abc)"},{"CollaboratorName":"(Xyz)"}]'
     *  - A plain string: "Abc, Xyz"
     *  - An array of objects
     *  - Null / undefined
     */
    const parseCollaborators = (raw) => {
        if (!raw) return '-';
        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed.startsWith('[')) {
                try {
                    const arr = JSON.parse(trimmed);
                    if (Array.isArray(arr)) {
                        const names = arr
                            .map(o => o?.CollaboratorName || o?.Name || o?.name || '')
                            .filter(Boolean);
                        return names.length ? names.join(', ') : '-';
                    }
                } catch (e) { /* fall through to plain string */ }
            }
            return trimmed || '-';
        }
        if (Array.isArray(raw)) {
            return raw.map(o => o?.CollaboratorName || o?.Name || String(o)).filter(Boolean).join(', ') || '-';
        }
        return '-';
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200">
            {/* Header Tabs */}
            <div className="flex items-center border-b border-gray-200 px-4">
                <button
                    onClick={() => setSubTab('physical')}
                    className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${subTab === 'physical' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Physical Meeting
                </button>
                <button
                    onClick={() => setSubTab('virtual')}
                    className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${subTab === 'virtual' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Virtual
                </button>
            </div>

            {/* Tab Content */}
            <div className="p-0">
                {subTab === 'physical' && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Owner Detail</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {physicalAppts.map((appt, idx) => {
                                    // Field mapping aligned to actual API response keys or our default/fallback mock data
                                    const title = appt.Title || appt.TaskName || appt.EventName || `Appointment ${idx + 1}`;
                                    const time = appt.StartDate || appt.EventDate || appt.CreatedDate || appt.Date || 'No Date';
                                    const location = appt.Address || appt.Location || 'No location specified';
                                    const owner = appt.OwnerName || appt.CreatedBy || appt.UserLogin || 'Unknown';
                                    const fieldExecs = (appt.FieldExecutives && appt.FieldExecutives !== '') ? appt.FieldExecutives : owner;
                                    const collaborator = parseCollaborators(appt.CollaboratorName || appt.Collaborators);
                                    const status = appt.OutcomeName || appt.Status || appt.TaskStatus || 'Pending';
                                    const checkIn = (appt.CheckIn && appt.CheckIn !== '') ? appt.CheckIn : '-';
                                    const checkOut = (appt.CheckOut && appt.CheckOut !== '') ? appt.CheckOut : '-';

                                    return (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            {/* Details Column */}
                                            <td className="px-4 py-4 w-5/12 align-top">
                                                <h3 className="text-sm font-bold text-gray-900 mb-2">{title}</h3>
                                                <div className="flex flex-col gap-2 mt-2">
                                                    <div className="flex items-start gap-2">
                                                        <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" title="Date" />
                                                        <span className="text-sm text-gray-600">{time}</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <MapPin className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" title="Location" />
                                                        <span className="text-sm text-gray-600 leading-tight">{location}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Owner Detail Column */}
                                            <td className="px-4 py-4 w-4/12 align-top">
                                                <div className="flex flex-col gap-3">
                                                    <div className="flex items-center gap-2">
                                                        <Users className="w-4 h-4 text-indigo-500 flex-shrink-0" title="Field Executives" />
                                                        <span className="text-sm text-gray-600 truncate">{fieldExecs}</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <UserCheck className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" title="Collaborators" />
                                                        <span className="text-sm text-gray-600 break-words">{collaborator}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-amber-500 flex-shrink-0" title="Owner" />
                                                        <span className="text-sm text-gray-600 truncate">{owner}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Status Column */}
                                            <td className="px-4 py-4 w-3/12 align-top">
                                                <div className="flex flex-col gap-3">
                                                    <div className="flex items-center gap-2">
                                                        <LogIn className="w-4 h-4 text-green-600 flex-shrink-0" title="Check-in" />
                                                        <span className="text-sm text-gray-600">{checkIn}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <LogOut className="w-4 h-4 text-red-600 flex-shrink-0" title="Check-out" />
                                                        <span className="text-sm text-gray-600">{checkOut}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" title="Outcome" />
                                                        <span className="text-sm text-gray-800 font-medium">{status}</span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {subTab === 'virtual' && (
                    <div className="p-4">
                        <div className="flex items-center justify-end mb-4">
                            <div className="flex items-center gap-4 text-sm bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="virtualSort"
                                        value="duedate"
                                        checked={virtualSort === 'duedate'}
                                        onChange={() => setVirtualSort('duedate')}
                                        className="text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Due Date</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="virtualSort"
                                        value="modifydate"
                                        checked={virtualSort === 'modifydate'}
                                        onChange={() => setVirtualSort('modifydate')}
                                        className="text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Modified Date</span>
                                </label>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {virtualAppts.map((appt, idx) => (
                                <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <Calendar className="w-6 h-6 text-blue-500 flex-shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-gray-900 font-medium">{appt.Title || appt.TaskName || appt.EventName || 'Virtual Appointment'}</p>
                                        <p className="text-sm text-gray-500">{appt.StartDate || appt.EventDate || appt.CreatedDate || appt.Date}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
